import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { useSiteContent } from "./siteContent";

const dropLinkCls = "block px-4 py-3 text-sm font-semibold text-slate-300 hover:bg-[#00D2C2]/15 hover:text-[#00D2C2] transition-colors";
const navLinkCls = "nav-link text-sm font-semibold text-slate-300 hover:text-[#00D2C2] transition-colors";

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

function useNavLinks() {
  const site = useSiteContent();
  const links = (site.nav_links || []).filter((n) => {
    if (n.on === false) return false;
    if (n.page === "blog") return site.blog_settings?.enabled !== false;
    return !n.page || site.pages?.[n.page] !== false;
  });
  return { site, links };
}

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const { site, links } = useNavLinks();
  const loc = useLocation();
  useEffect(() => {
    try {
      if (localStorage.getItem("wems_no_track") === "1") return;
      let vid = localStorage.getItem("wems_vid");
      if (!vid) {
        vid = crypto.randomUUID();
        localStorage.setItem("wems_vid", vid);
      }
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/site-track`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: loc.pathname, vid }),
      }).catch(() => {});
    } catch { /* tracking is best-effort */ }
  }, [loc.pathname]);
  const header = site.header || {};
  const logo = header.logo || "/site-assets/logo.png";
  const svcLinks = [
    { label: "All services", to: "/services", tid: "site-nav-svc-all" },
    ...(site.services_catalog || []).map((s) => ({ label: s.name, to: `/services/${s.slug}`, tid: `site-nav-svc-${s.slug}` })),
  ];

  const desktopItem = (n, i) => {
    if (n.auto_services) {
      return (
        <div key={`svc-${i}`} className="relative" onMouseLeave={() => setSvcOpen(false)}>
          <button onClick={() => setSvcOpen(!svcOpen)} data-testid="site-nav-services"
            className={`${navLinkCls} inline-flex items-center gap-1.5`}>
            {n.label} <ChevronDown size={14} className={`transition-transform ${svcOpen ? "rotate-180" : ""}`} />
          </button>
          {svcOpen && (
            <div className="absolute left-0 top-full pt-2 w-60">
              <div className="rounded-xl bg-[#121820] border border-slate-800 shadow-2xl overflow-hidden py-1" data-testid="site-services-menu">
                {svcLinks.map((s) => (
                  <Link key={s.to} to={s.to} onClick={() => setSvcOpen(false)} data-testid={s.tid} className={dropLinkCls}>{s.label}</Link>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }
    return (
      <Link key={`${n.to}-${i}`} to={n.to || "/"} data-testid={n.tid || `site-nav-${slugify(n.label)}`} className={navLinkCls}>
        {n.label}
      </Link>
    );
  };

  const mobileLinks = [];
  links.forEach((n) => {
    if (n.auto_services) {
      svcLinks.forEach((s) => mobileLinks.push({ label: s.label, to: s.to, mtestid: s.tid.replace("site-nav", "site-mnav") }));
    } else {
      mobileLinks.push({ label: n.label, to: n.to || "/", mtestid: `site-mnav-${slugify(n.label)}` });
    }
  });

  return (
    <header className="bg-[#0B0F14]/90 backdrop-blur-xl sticky top-0 z-50 border-b border-white/10 shadow-[0_4px_20px_rgba(0,0,0,0.5)]" data-testid="site-header">
      <div className="container-x py-3.5 flex items-center gap-4">
        <Link to="/" className="flex items-center min-w-0" data-testid="site-logo" aria-label="Wales Event Medical Services — home">
          <span className="bg-white rounded-xl px-2.5 py-1.5 inline-flex items-center shrink-0 border border-slate-600/50 shadow-md">
            <img src={logo} alt="Wales Event Medical Services" className="h-10 w-auto" />
          </span>
        </Link>
        <nav className="hidden lg:flex items-center gap-6 ml-auto">
          {links.map(desktopItem)}
          <div className="relative" onMouseLeave={() => setLoginOpen(false)}>
            <button onClick={() => setLoginOpen(!loginOpen)} data-testid="site-login-btn"
              className={`${navLinkCls} inline-flex items-center gap-1.5`}>
              Login <ChevronDown size={14} className={`transition-transform ${loginOpen ? "rotate-180" : ""}`} />
            </button>
            {loginOpen && (
              <div className="absolute right-0 top-full pt-2 w-44">
                <div className="rounded-xl bg-[#121820] border border-slate-800 shadow-2xl overflow-hidden py-1" data-testid="site-login-menu">
                  <Link to="/portal/login" onClick={() => setLoginOpen(false)} data-testid="site-login-client" className={dropLinkCls}>Client login</Link>
                  <Link to="/staff/login" onClick={() => setLoginOpen(false)} data-testid="site-login-staff" className={dropLinkCls}>Staff login</Link>
                </div>
              </div>
            )}
          </div>
          {header.cta_label !== "" && (
            <Link to={header.cta_to || "/intake"} className="btn btn-primary !py-2.5" data-testid="site-nav-quote">
              {header.cta_label || "Get a Free Quote"} <ArrowUpRight size={16} />
            </Link>
          )}
        </nav>
        <button className="lg:hidden ml-auto h-11 w-11 grid place-items-center rounded-lg text-slate-200 hover:bg-white/10"
          onClick={() => setOpen(!open)} aria-label="Menu" data-testid="site-menu-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-white/10 px-4 py-4 space-y-1 bg-[#0B0F14]" data-testid="site-mobile-nav">
          {[...mobileLinks,
            { label: "Client login", to: "/portal/login", mtestid: "site-mnav-client-login" },
            { label: "Staff login", to: "/staff/login", mtestid: "site-mnav-staff-login" },
            { label: header.cta_label || "Get a Free Quote", to: header.cta_to || "/intake", mtestid: "site-mnav-quote" }].map((n) => (
            <Link key={n.to + n.label} to={n.to} onClick={() => setOpen(false)} data-testid={n.mtestid}
              className="block px-2 py-2.5 rounded-lg text-sm font-semibold text-slate-300 hover:bg-white/5 hover:text-[#00D2C2]">
              {n.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}

export function SiteFooter() {
  const { site, links } = useNavLinks();
  const contact = site.contact_info || {};
  const footer = site.footer || {};
  return (
    <footer className="bg-[#070A0F] text-slate-300 border-t border-slate-800/80" data-testid="site-footer">
      <div className="container-x py-14 grid gap-10 md:grid-cols-4">
        <div>
          <span className="bg-white rounded-xl px-3 py-2 inline-flex items-center border border-slate-600/50 shadow-md mb-4">
            <img src={(site.header || {}).logo || "/site-assets/logo.png"} alt="Wales Event Medical Services" className="h-11 w-auto" />
          </span>
          <p className="text-sm text-slate-400 leading-relaxed" data-testid="site-footer-tagline">
            {footer.tagline || "Expert medical cover for events across the UK — from first responders to doctors."}
          </p>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#00D2C2] mb-3">Explore</p>
          <div className="space-y-2">
            {links.map((n) => (
              <Link key={n.to + n.label} to={n.to || "/"} className="block text-sm text-slate-300 hover:text-[#00D2C2] transition-colors" data-testid={`site-footer-${slugify(n.label)}`}>{n.label}</Link>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#00D2C2] mb-3">Portals</p>
          <div className="space-y-2">
            <Link to="/portal/login" className="block text-sm text-slate-300 hover:text-[#00D2C2] transition-colors" data-testid="site-footer-client-login">Client login</Link>
            <Link to="/staff/login" className="block text-sm text-slate-300 hover:text-[#00D2C2] transition-colors" data-testid="site-footer-staff-login">Staff login</Link>
            <Link to="/join" className="block text-sm text-slate-300 hover:text-[#00D2C2] transition-colors" data-testid="site-footer-join">Join the team</Link>
            <Link to="/intake" className="block text-sm text-slate-300 hover:text-[#00D2C2] transition-colors" data-testid="site-footer-quote">Get a free quote</Link>
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#00D2C2] mb-3">Contact</p>
          <a href={`mailto:${contact.email || "info@waleseventmedical.co.uk"}`} className="block text-sm text-slate-300 hover:text-[#00D2C2] transition-colors" data-testid="site-footer-email">
            {contact.email || "info@waleseventmedical.co.uk"}
          </a>
          {contact.phone && <p className="text-sm mt-2 text-slate-400">{contact.phone}</p>}
          <p className="text-sm mt-2 text-slate-400">{contact.location || "Based in Wales, covering the whole UK"}</p>
        </div>
      </div>
      <div className="border-t border-white/10 bg-[#040609]">
        <div className="container-x py-5 text-xs text-slate-500 flex flex-wrap gap-x-6 gap-y-1 justify-between">
          <span>© {new Date().getFullYear()} Wales Event Medical Services. All rights reserved.</span>
          <span data-testid="site-footer-note">{footer.bottom_note || "CQC-standard clinical governance · DBS-checked medics"}</span>
        </div>
      </div>
    </footer>
  );
}

export function PageHero({ title, subtitle, image }) {
  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/60 to-slate-900/25" />
      <div className="container-x relative z-10">
        <h1 className="font-serif-site text-white text-4xl md:text-5xl font-extrabold tracking-tight" data-testid="site-page-title">{title}</h1>
        <p className="mt-4 text-lg text-slate-200 max-w-2xl leading-relaxed">{subtitle}</p>
      </div>
    </section>
  );
}
